import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import { emailValidationMessage, normalizeEmail } from '../utils/emailValidation';
import RecaptchaWidget from '../components/auth/RecaptchaWidget';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const initialEmail = searchParams.get('email') || '';
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [tokenError, setTokenError] = useState(false);
  const [resent, setResent] = useState(false);
  const getCaptchaTokenRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    api.post('/api/auth/verify-email', { token })
      .then(() => {
        if (!cancelled) {
          setVerified(true);
          toast.success('Email verified — you can sign in now');
        }
      })
      .catch(() => {
        if (!cancelled) setTokenError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    const msg = emailValidationMessage(email);
    if (msg) {
      toast.error(msg);
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/auth/resend-verification', {
        email: normalizeEmail(email),
        recaptcha_token: getCaptchaTokenRef.current?.() || undefined,
      });
      setResent(true);
    } catch (err) {
      toast.error(err?.message || 'Could not send verification email — try again shortly');
    } finally {
      setLoading(false);
    }
  };

  if (token && loading && !verified && !tokenError) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center">
          <p className="text-slate-300 text-sm">Verifying your email…</p>
        </div>
      </div>
    );
  }

  if (verified) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center">
          <h1 className="text-xl font-semibold text-white mb-3">Email verified</h1>
          <p className="text-slate-500 text-sm mb-6">Your account is ready. Sign in with your password.</p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="text-emerald-400 hover:underline text-sm"
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  if (token && tokenError) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8">
          <h1 className="text-xl font-semibold text-white mb-2 text-center">Link expired or invalid</h1>
          <p className="text-slate-500 text-sm text-center mb-6">
            Request a new verification email below.
          </p>
          <ResendForm
            email={email}
            setEmail={setEmail}
            loading={loading}
            resent={resent}
            onSubmit={handleResend}
            getCaptchaTokenRef={getCaptchaTokenRef}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8">
        <Link to="/" className="block">
          <img src="/propscope-logo.png" alt="PropScope"
            className="h-24 w-auto object-contain mx-auto mb-2 hover:opacity-90 transition-opacity" />
        </Link>
        <h1 className="text-xl font-semibold text-white mb-2 text-center">Verify your email</h1>
        <p className="text-slate-500 text-sm text-center mb-6">
          We sent a verification link to your inbox. Open it to activate your account, or resend below.
        </p>
        <ResendForm
          email={email}
          setEmail={setEmail}
          loading={loading}
          resent={resent}
          onSubmit={handleResend}
          getCaptchaTokenRef={getCaptchaTokenRef}
        />
        <p className="text-center text-slate-500 text-sm mt-6">
          <Link to="/login" className="text-emerald-400 hover:underline">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}

function ResendForm({ email, setEmail, loading, resent, onSubmit, getCaptchaTokenRef }) {
  if (resent) {
    return (
      <div className="text-center space-y-4">
        <p className="text-slate-300 text-sm">
          If <span className="text-white">{normalizeEmail(email)}</span> is registered and not yet verified,
          you will receive a new verification link.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label htmlFor="verify-email" className="block text-sm text-slate-400 mb-1">Email</label>
        <input
          id="verify-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="username"
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
        />
      </div>
      <RecaptchaWidget
        onReady={(getter) => { getCaptchaTokenRef.current = getter; }}
        disabled={loading}
      />
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg py-2.5 font-medium transition-colors disabled:opacity-50"
      >
        {loading ? 'Sending...' : 'Resend verification email'}
      </button>
    </form>
  );
}
